export type TreeItemType =
    | 'Unknown'
    | 'System'
    | 'Assembly'
    | 'Asset'
    | 'MonitoringPoint'
    | 'IndicatorGroup'
    | 'Group'
    | 'AnalyticModel';

// Tree item for KCF Group Trees
export type TreeItem = {
    id: string;
    name: string;
    abbreviation?: string;
    type: TreeItemType;
    depth: number;
    hasAlarms: boolean;
    hasWarnings: boolean;
    isPaused?: boolean;
    parent?: TreeItem;
};

// Interface to kendo items in tree view
export interface IKendoTreeViewItem<T> {
    model: T;
    text?: string;
    items: IKendoTreeViewItem<T>[];
    expanded?: boolean;
    selected?: boolean;
    disabled?: boolean;
    checked?: boolean;
    checkedIndeterminate?: boolean;
    hasChildren?: boolean;
}

export type Tree<Data> = IKendoTreeViewItem<Data>[];

export type GroupTreeItem = IKendoTreeViewItem<TreeItem>;

/**
 * Returns the first tree item in the hierarchy (include self) that meets the
 * supplied criteria, or `undefined` if none is found.
 *
 * @param treeItem The starting tree item.
 * @param where The criteria that must be met for the supplied item or parent.
 */
export function findInHierarchy(
    treeItem: TreeItem,
    where: (item: TreeItem) => boolean
): TreeItem | undefined {
    if (where(treeItem)) {
        return treeItem;
    }
    const { parent } = treeItem;
    return parent ? findInHierarchy(parent, where) : undefined;
}

/**
 * Generates the structure of the kendo tree from file paths.
 * Tree is build first seen basis so if you want to order your tree
 * order the paths before passing in.
 * @param paths Array of path to files.
 * @param createItem Creates object for kendo tree
 */
export function generateFileTreeStructure<T>(
    paths: string[],
    createItem: (
        name: string,
        path: string,
        children: IKendoTreeViewItem<T>[]
    ) => IKendoTreeViewItem<T>
) {
    const result: IKendoTreeViewItem<T>[] = [];
    const level = { result };

    paths.forEach((path) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        path.split('/').reduce((r: any, name) => {
            if (!r[name]) {
                r[name] = { result: [] };
                r.result.push(createItem(name, path, r[name].result));
            }

            return r[name];
        }, level);
    });
    return result;
}

/**
 * Searches the tree for a node with a particular ID, using breadth-first search.
 * @param tree The tree to search.
 * @param id The node ID to find.
 * @returns The found node, or null.
 */
export const search = <Data extends { id: string }>(
    tree: Tree<Data>,
    id: string
): IKendoTreeViewItem<Data> | null => {
    const queue = [...tree];
    const explored = new Set<string>();
    while (queue.length > 0) {
        const item = queue.pop();
        if (!item) {
            return null;
        }
        if (item.model.id === id) {
            return item;
        }
        item.items.forEach((child) => {
            if (!explored.has(child.model.id)) {
                explored.add(child.model.id);
                queue.push(child);
            }
        });
    }
    return null;
};

/**
 * Searches the tree for nodes with a particular ID, using breadth-first search.
 * @param tree The tree to search.
 * @param id The node ID to find.
 * @returns The found nodes, or null.
 */
export const searchMany = <Data extends { id: string }>(
    tree: Tree<Data>,
    id: string
): IKendoTreeViewItem<Data>[] | null => {
    const queue = [...tree];
    const result: IKendoTreeViewItem<Data>[] = [];
    while (queue.length > 0) {
        const item = queue.pop();
        if (!item) {
            return null;
        }
        if (item.model.id === id) {
            result.push(item);
        }
        item.items.forEach((child) => {
            queue.push(child);
        });
    }
    return result.length > 0 ? result : null;
};

/**
 * Searches the tree for a node with a particular ID, using same method as `search`,
 * but throws an error if the node does not exist.
 * @param tree The tree to search.
 * @param id The node ID to find.
 * @returns The found node.
 * @throws Error
 */
export const find = <Data extends { id: string }>(
    tree: Tree<Data>,
    id: string
): IKendoTreeViewItem<Data> => {
    const item = search(tree, id);
    if (!item) {
        throw new Error(
            `Tried to search for tree item ID '${id}' but it was not found in the tree.`
        );
    }
    return item;
};

/**
 * Searches the tree for nodes with a particular ID, using same method as `search`,
 * but throws an error if the node does not exist.
 * @param tree The tree to search.
 * @param id The node ID to find.
 * @returns The found nodes.
 * @throws Error
 */
export const findMany = <Data extends { id: string }>(
    tree: Tree<Data>,
    id: string
): IKendoTreeViewItem<Data>[] => {
    const items = searchMany(tree, id);
    if (!items) {
        throw new Error(
            `Tried to search for tree item ID '${id}' but it was not found in the tree.`
        );
    }
    return items;
};
